/**
 * PolicyAdminTable — create/edit/version/archive/delete policies + audit history.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Pencil, Upload, History, Archive, ArchiveRestore, Trash2,
  FileText, Search, RefreshCw, Eye
} from 'lucide-react';
import { PolicyViewerDialog } from './PolicyViewerDialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';
import { formatDateOnly, formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const emptyForm = {
  title: '', description: '', category: 'HR', owner_name: '',
  effective_date: '', review_date: '', ack_required: true, ack_deadline_days: 14,
  assignment_type: 'all', team_ids: [], roles: [], user_ids: [],
};

const fmtDate = (d) => d ? formatDateOnly(d) : '—';

const assignmentLabel = (a, options) => {
  if (!a) return '—';
  if (a.type === 'all') return 'All Employees';
  if (a.type === 'teams') {
    const names = (a.team_ids || []).map((id) => options?.teams?.find((t) => t.id === id)?.name || id);
    return `Teams: ${names.join(', ')}`;
  }
  if (a.type === 'roles') return `Roles: ${(a.roles || []).map((r) => r.replace(/_/g, ' ')).join(', ')}`;
  if (a.type === 'users') return `${(a.user_ids || []).length} user(s)`;
  return a.type;
};

const MultiPick = ({ items, selected, onToggle, labelKey = 'name', testId }) => (
  <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1" data-testid={testId}>
    {items.map((it) => {
      const id = it.id || it;
      const label = typeof it === 'string' ? it.replace(/_/g, ' ') : (it[labelKey] || it.full_name || it.email);
      return (
        <label key={id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer py-0.5">
          <Checkbox checked={selected.includes(id)} onCheckedChange={() => onToggle(id)} />
          <span className="capitalize">{label}</span>
        </label>
      );
    })}
    {items.length === 0 && <p className="text-xs text-slate-400 py-1">No options available</p>}
  </div>
);

export default function PolicyAdminTable() {
  const [policies, setPolicies] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [editor, setEditor] = useState(null);       // {mode:'create'|'edit', id?, form, file, versionLabel}
  const [saving, setSaving] = useState(false);
  const [versionDlg, setVersionDlg] = useState(null); // {policy, file, label, reack, notes}
  const [auditDlg, setAuditDlg] = useState(null);     // {policy, entries, acks, loading}
  const [confirmDlg, setConfirmDlg] = useState(null); // {policy, action, reason}
  const [viewPolicy, setViewPolicy] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, oRes] = await Promise.all([
        axios.get(`${API}/policies`),
        axios.get(`${API}/policies/meta/options`),
      ]);
      setPolicies(pRes.data.policies || []);
      setOptions(oRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => setEditor({ mode: 'create', form: { ...emptyForm }, file: null, versionLabel: '1.0' });
  const openEdit = (p) => setEditor({
    mode: 'edit', id: p.id,
    form: {
      title: p.title, description: p.description || '', category: p.category,
      owner_name: p.owner_name || '', effective_date: p.effective_date || '',
      review_date: p.review_date || '', ack_required: p.ack_required,
      ack_deadline_days: p.ack_deadline_days || 14,
      assignment_type: p.assignment?.type || 'all',
      team_ids: p.assignment?.team_ids || [], roles: p.assignment?.roles || [],
      user_ids: p.assignment?.user_ids || [],
    },
  });

  const setForm = (patch) => setEditor((e) => ({ ...e, form: { ...e.form, ...patch } }));
  const toggleIn = (key, id) => setForm({
    [key]: editor.form[key].includes(id)
      ? editor.form[key].filter((x) => x !== id)
      : [...editor.form[key], id],
  });

  const buildPayload = (f) => ({
    title: f.title, description: f.description || null, category: f.category,
    owner_name: f.owner_name,
    effective_date: f.effective_date || null, review_date: f.review_date || null,
    ack_required: f.ack_required, ack_deadline_days: parseInt(f.ack_deadline_days, 10) || 14,
    assignment: {
      type: f.assignment_type,
      team_ids: f.assignment_type === 'teams' ? f.team_ids : [],
      roles: f.assignment_type === 'roles' ? f.roles : [],
      user_ids: f.assignment_type === 'users' ? f.user_ids : [],
    },
  });

  const validate = (f) => {
    if (!f.title || f.title.trim().length < 2) return 'Title is required';
    if (!f.owner_name || f.owner_name.trim().length < 2) return 'Policy owner is required';
    if (f.assignment_type === 'teams' && f.team_ids.length === 0) return 'Select at least one team';
    if (f.assignment_type === 'roles' && f.roles.length === 0) return 'Select at least one role';
    if (f.assignment_type === 'users' && f.user_ids.length === 0) return 'Select at least one user';
    return null;
  };

  const handleSave = async () => {
    const err = validate(editor.form);
    if (err) { toast.error(err); return; }
    if (editor.mode === 'create' && !editor.file) { toast.error('Please attach the policy document'); return; }
    try {
      setSaving(true);
      if (editor.mode === 'create') {
        const res = await axios.post(`${API}/policies`, buildPayload(editor.form));
        const fd = new FormData();
        fd.append('file', editor.file);
        fd.append('version_label', editor.versionLabel || '1.0');
        fd.append('requires_reack', 'false');
        await axios.post(`${API}/policies/${res.data.id}/versions`, fd);
        toast.success('Policy created, published and assigned');
      } else {
        await axios.put(`${API}/policies/${editor.id}`, buildPayload(editor.form));
        toast.success('Policy updated');
      }
      setEditor(null);
      fetchAll();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save policy');
    } finally { setSaving(false); }
  };

  const handleVersionUpload = async () => {
    if (!versionDlg.file) { toast.error('Please attach the new document'); return; }
    if (!versionDlg.label?.trim()) { toast.error('Version number is required'); return; }
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('file', versionDlg.file);
      fd.append('version_label', versionDlg.label.trim());
      fd.append('requires_reack', versionDlg.reack ? 'true' : 'false');
      if (versionDlg.notes) fd.append('notes', versionDlg.notes);
      await axios.post(`${API}/policies/${versionDlg.policy.id}/versions`, fd);
      toast.success(`Version ${versionDlg.label} published${versionDlg.reack ? ' — re-acknowledgment required' : ''}`);
      setVersionDlg(null);
      fetchAll();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to upload version');
    } finally { setSaving(false); }
  };

  const handleConfirmAction = async () => {
    const { policy, action, reason } = confirmDlg;
    try {
      setSaving(true);
      if (action === 'archive') await axios.post(`${API}/policies/${policy.id}/archive`, { reason: reason || null });
      else if (action === 'unarchive') await axios.post(`${API}/policies/${policy.id}/unarchive`);
      else if (action === 'delete') await axios.delete(`${API}/policies/${policy.id}`);
      toast.success(`Policy ${action}d`);
      setConfirmDlg(null);
      fetchAll();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || `Failed to ${action} policy`);
    } finally { setSaving(false); }
  };

  const openAudit = async (p) => {
    setAuditDlg({ policy: p, entries: [], acks: [], loading: true });
    try {
      const [aRes, kRes] = await Promise.all([
        axios.get(`${API}/policies/${p.id}/audit`),
        axios.get(`${API}/policies/${p.id}/acknowledgments`),
      ]);
      setAuditDlg({ policy: p, entries: aRes.data.entries || [], acks: kRes.data.acknowledgments || [], loading: false });
    } catch {
      toast.error('Failed to load audit history');
      setAuditDlg(null);
    }
  };

  const filtered = policies.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (search && !`${p.title} ${p.category} ${p.owner_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const f = editor?.form;

  return (
    <div className="space-y-4" data-testid="policy-admin-table">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="policy-create-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Policy
        </Button>
        <div className="relative ml-auto">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search policies…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-60 h-9" data-testid="policy-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9" data-testid="policy-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="policy-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <Table data-testid="policies-table" className="tf-blue-head">
              <TableHeader>
                <TableRow className="bg-blue-600 hover:bg-blue-600 border-blue-600">
                  {['Policy', 'Category', 'Version', 'Assigned To', 'Acknowledgment', 'Compliance', 'Effective', 'Status', 'Actions'].map((h, i) => (
                    <TableHead key={h} className={`!text-white text-xs font-semibold uppercase tracking-wider ${i === 8 ? 'text-right' : ''}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">No policies yet. Click "New Policy" to create the first one.</TableCell></TableRow>
                )}
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`policy-row-${p.id}`} className={p.status === 'archived' ? 'opacity-60' : ''}>
                    <TableCell>
                      <p className="text-sm font-semibold text-slate-900">{p.title}</p>
                      <p className="text-xs text-slate-400">Owner: {p.owner_name || '—'}</p>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{p.category}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-700">{p.current_version_label ? `v${p.current_version_label}` : '—'}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[180px]">{assignmentLabel(p.assignment, options)}</TableCell>
                    <TableCell>
                      {p.ack_required
                        ? <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100 text-xs">Required · {p.ack_deadline_days}d</Badge>
                        : <Badge className="bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100 text-xs">Info only</Badge>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {p.ack_required ? `${p.acknowledged_count}/${p.assigned_count}` : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDate(p.effective_date)}</TableCell>
                    <TableCell>
                      {p.status === 'active'
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-xs">Active</Badge>
                        : <Badge className="bg-slate-200 text-slate-600 border-slate-300 hover:bg-slate-200 text-xs">Archived</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        {p.current_version_id && (
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-blue-600" title="View document" onClick={() => setViewPolicy(p)} data-testid={`policy-view-btn-${p.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-blue-600" title="Edit policy" onClick={() => openEdit(p)} data-testid={`policy-edit-btn-${p.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-indigo-600" title="Upload new version" onClick={() => setVersionDlg({ policy: p, file: null, label: '', reack: false, notes: '' })} data-testid={`policy-version-btn-${p.id}`}>
                          <Upload className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-indigo-600" title="Audit history" onClick={() => openAudit(p)} data-testid={`policy-audit-btn-${p.id}`}>
                          <History className="h-4 w-4" />
                        </Button>
                        {p.status === 'active' ? (
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-amber-600" title="Archive" onClick={() => setConfirmDlg({ policy: p, action: 'archive', reason: '' })} data-testid={`policy-archive-btn-${p.id}`}>
                            <Archive className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-emerald-600" title="Restore" onClick={() => setConfirmDlg({ policy: p, action: 'unarchive' })} data-testid={`policy-unarchive-btn-${p.id}`}>
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-red-600" title="Delete" onClick={() => setConfirmDlg({ policy: p, action: 'delete' })} data-testid={`policy-delete-btn-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!editor} onOpenChange={(o) => { if (!o && !saving) setEditor(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.mode === 'create' ? 'New Policy' : `Edit — ${f?.title}`}</DialogTitle>
            <DialogDescription>
              {editor?.mode === 'create'
                ? 'The policy is published and assigned immediately after creation.'
                : 'Metadata and assignment changes take effect immediately. Use "Upload new version" to replace the document.'}
            </DialogDescription>
          </DialogHeader>
          {f && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Title *</label>
                <Input value={f.title} onChange={(e) => setForm({ title: e.target.value })} data-testid="policy-title-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
                <Textarea rows={2} value={f.description} onChange={(e) => setForm({ description: e.target.value })} data-testid="policy-description-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Category *</label>
                <Select value={f.category} onValueChange={(v) => setForm({ category: v })}>
                  <SelectTrigger data-testid="policy-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Policy Owner *</label>
                <Input value={f.owner_name} onChange={(e) => setForm({ owner_name: e.target.value })} placeholder="e.g., HR Department" data-testid="policy-owner-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Effective Date</label>
                <Input type="date" value={f.effective_date} onChange={(e) => setForm({ effective_date: e.target.value })} data-testid="policy-effective-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Review Date</label>
                <Input type="date" value={f.review_date} onChange={(e) => setForm({ review_date: e.target.value })} data-testid="policy-review-input" />
              </div>
              <div className="col-span-2 border-t border-slate-100 pt-3">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Acknowledgment</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" checked={f.ack_required} onChange={() => setForm({ ack_required: true })} data-testid="policy-ack-required-radio" />
                    Acknowledgment required
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" checked={!f.ack_required} onChange={() => setForm({ ack_required: false })} data-testid="policy-info-only-radio" />
                    Information only
                  </label>
                  {f.ack_required && (
                    <span className="flex items-center gap-2 text-sm text-slate-600 ml-auto">
                      Acknowledge within
                      <Input type="number" min={1} max={365} className="w-20 h-8" value={f.ack_deadline_days}
                        onChange={(e) => setForm({ ack_deadline_days: e.target.value })} data-testid="policy-deadline-input" />
                      days
                    </span>
                  )}
                </div>
              </div>
              <div className="col-span-2 border-t border-slate-100 pt-3">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Assign To *</label>
                <Select value={f.assignment_type} onValueChange={(v) => setForm({ assignment_type: v })}>
                  <SelectTrigger data-testid="policy-assignment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    <SelectItem value="teams">Specific Teams</SelectItem>
                    <SelectItem value="roles">Specific Roles</SelectItem>
                    <SelectItem value="users">Individual Users</SelectItem>
                  </SelectContent>
                </Select>
                <div className="mt-2">
                  {f.assignment_type === 'teams' && <MultiPick items={options?.teams || []} selected={f.team_ids} onToggle={(id) => toggleIn('team_ids', id)} testId="policy-team-picker" />}
                  {f.assignment_type === 'roles' && <MultiPick items={options?.roles || []} selected={f.roles} onToggle={(id) => toggleIn('roles', id)} testId="policy-role-picker" />}
                  {f.assignment_type === 'users' && <MultiPick items={options?.users || []} selected={f.user_ids} onToggle={(id) => toggleIn('user_ids', id)} labelKey="full_name" testId="policy-user-picker" />}
                </div>
              </div>
              {editor.mode === 'create' && (
                <div className="col-span-2 border-t border-slate-100 pt-3 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Policy Document * (PDF, DOCX… max 10 MB)</label>
                    <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                      onChange={(e) => setEditor((ed) => ({ ...ed, file: e.target.files?.[0] || null }))}
                      data-testid="policy-file-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Version Number *</label>
                    <Input value={editor.versionLabel} onChange={(e) => setEditor((ed) => ({ ...ed, versionLabel: e.target.value }))} data-testid="policy-version-label-input" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="policy-save-btn">
              {saving ? 'Saving…' : editor?.mode === 'create' ? 'Create & Publish' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload new version dialog */}
      <Dialog open={!!versionDlg} onOpenChange={(o) => { if (!o && !saving) setVersionDlg(null); }}>
        <DialogContent data-testid="policy-version-dialog">
          <DialogHeader>
            <DialogTitle>Upload New Version — {versionDlg?.policy?.title}</DialogTitle>
            <DialogDescription>
              Current: {versionDlg?.policy?.current_version_label ? `v${versionDlg.policy.current_version_label}` : 'no document'}.
              Previous versions and acknowledgments are preserved in history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">New Document *</label>
              <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                onChange={(e) => setVersionDlg((d) => ({ ...d, file: e.target.files?.[0] || null }))}
                data-testid="version-file-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Version Number *</label>
              <Input placeholder="e.g., 2.0" value={versionDlg?.label || ''} onChange={(e) => setVersionDlg((d) => ({ ...d, label: e.target.value }))} data-testid="version-label-input" />
            </div>
            {versionDlg?.policy?.ack_required && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Re-acknowledgment</label>
                <label className="flex items-center gap-2 text-sm cursor-pointer mb-1">
                  <input type="radio" checked={!!versionDlg?.reack} onChange={() => setVersionDlg((d) => ({ ...d, reack: true }))} data-testid="version-reack-yes" />
                  Re-acknowledgment required (material change — everyone returns to Pending)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={!versionDlg?.reack} onChange={() => setVersionDlg((d) => ({ ...d, reack: false }))} data-testid="version-reack-no" />
                  No re-acknowledgment required (minor formatting/wording change)
                </label>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Change Notes</label>
              <Textarea rows={2} value={versionDlg?.notes || ''} onChange={(e) => setVersionDlg((d) => ({ ...d, notes: e.target.value }))} data-testid="version-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionDlg(null)} disabled={saving}>Cancel</Button>
            <Button onClick={handleVersionUpload} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="version-upload-btn">
              {saving ? 'Publishing…' : 'Publish Version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive / delete confirm dialog */}
      <Dialog open={!!confirmDlg} onOpenChange={(o) => { if (!o && !saving) setConfirmDlg(null); }}>
        <DialogContent data-testid="policy-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{confirmDlg?.action} Policy — {confirmDlg?.policy?.title}</DialogTitle>
            <DialogDescription>
              {confirmDlg?.action === 'archive' && 'Archived policies are hidden from employees but all historical records, versions and acknowledgments are preserved.'}
              {confirmDlg?.action === 'unarchive' && 'The policy will become visible to assigned employees again.'}
              {confirmDlg?.action === 'delete' && 'The policy will be removed from all views. Audit history and acknowledgment records are preserved for compliance.'}
            </DialogDescription>
          </DialogHeader>
          {confirmDlg?.action === 'archive' && (
            <Textarea rows={2} placeholder="Reason (optional, recorded in audit)" value={confirmDlg?.reason || ''}
              onChange={(e) => setConfirmDlg((d) => ({ ...d, reason: e.target.value }))} data-testid="archive-reason-input" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDlg(null)} disabled={saving}>Cancel</Button>
            <Button onClick={handleConfirmAction} disabled={saving}
              className={confirmDlg?.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
              data-testid="policy-confirm-btn">
              {saving ? 'Working…' : `Confirm ${confirmDlg?.action}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit history dialog */}
      {/* Read-only document viewer */}
      <PolicyViewerDialog policy={viewPolicy} onClose={() => setViewPolicy(null)} />

      {/* Audit dialog */}
      <Dialog open={!!auditDlg} onOpenChange={(o) => { if (!o) setAuditDlg(null); }}>
        <DialogContent className="max-w-2xl" data-testid="policy-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-indigo-600" /> Audit History — {auditDlg?.policy?.title}</DialogTitle>
            <DialogDescription>All policy events and acknowledgments, newest first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {auditDlg?.loading && [1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            {!auditDlg?.loading && auditDlg?.entries?.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">No audit entries.</p>
            )}
            {!auditDlg?.loading && auditDlg?.entries?.map((e) => (
              <div key={e.id} className="border border-slate-200 rounded-lg px-3 py-2" data-testid={`policy-audit-entry-${e.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase text-indigo-600">{e.action.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-slate-400">{formatDateTimeLocal(e.timestamp)}</span>
                </div>
                <p className="text-sm text-slate-700">{e.details}</p>
                {(e.previous_value || e.new_value) && (
                  <p className="text-xs text-slate-500">{e.previous_value || '—'} → {e.new_value || '—'}</p>
                )}
                <p className="text-xs text-slate-400">by {e.actor_name}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
